// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Identity ─────────────────────────────────────────────────────
/**
 * What an identity can be the identity *of*.
 *
 * A platform vocabulary, not an instrument's: these name kinds of thing an
 * electronic identity can attach to, the way `device` always has. Widened from
 * three kinds because identity regimes increasingly identify objects as well as
 * people — assets and goods, data and software, events and transactions,
 * locations — and an organisation holding evidence about those identities had
 * nowhere to put it. Which kinds an instrument *requires* is a question for that
 * instrument's pack, never for this list (R-46).
 *
 * `IDENTITY_HOLDER_SUBJECT_KINDS` names the kinds that can own or manage
 * another subject; see `IdentityProfile.holder_profile_id`.
 */
export const IDENTITY_SUBJECT_KINDS = [
    'natural_person',
    'legal_entity',
    'device',
    'physical_asset',
    'product',
    'digital_asset',
    'data_resource',
    'software',
    'digital_service',
    'event',
    'transaction',
    'location',
];
/** The subject kinds that can be recorded as another subject's owner or manager. */
export const IDENTITY_HOLDER_SUBJECT_KINDS = [
    'natural_person',
    'legal_entity',
];
/**
 * How the holder stands to the subject. Kept apart because ownership and the
 * right to manage or use a thing are different legal positions, and an identity
 * register that folded them into one "owner" field would record the wrong one
 * whenever they diverge.
 */
export const IDENTITY_HOLDER_ROLES = ['owner', 'manager'];
/**
 * The three states of a person's impersonation assessment, as display codes.
 * The stored field is `boolean | null`; this names each value so a label map
 * can key on a declared union rather than on strings it minted.
 */
export const IMPERSONATION_ASSESSMENT_STATES = ['unassessed', 'no_signs', 'signs_seen'];
export function impersonationAssessmentState(suspected) {
    return suspected === true ? 'signs_seen' : suspected === false ? 'no_signs' : 'unassessed';
}
/**
 * The one rule for a person's impersonation assessment, shared by identity
 * events and eKYC verifications so the two registers cannot disagree about it.
 *
 * `suspected` is tri-state — `null` not assessed, `false` assessed and not
 * suspected, `true` suspected. A suspicion needs a stated reason; a note needs
 * an assessment to be about. Returns the stored pair, or the reason it was
 * refused. The note is trimmed, and a blank note counts as none.
 */
export function resolveImpersonationAssessment(suspected, note) {
    const value = suspected ?? null;
    const text = typeof note === 'string' && note.trim().length > 0 ? note.trim() : null;
    if (value === true && text === null)
        return { ok: false, reason: 'note_required' };
    if (value === null && text !== null)
        return { ok: false, reason: 'note_without_assessment' };
    return { ok: true, impersonation_suspected: value, impersonation_note: text };
}
export const IDENTITY_PROFILE_STATUSES = ['active', 'suspended', 'closed'];
export const IDENTITY_ATTRIBUTE_STATUSES = [
    'unverified',
    'verified',
    'expired',
    'revoked',
    'disputed',
];
export const IDENTITY_EVENT_KINDS = ['attribute_verification', 'authentication'];
export const IDENTITY_EVENT_RESULTS = ['success', 'failure', 'inconclusive'];
// ── Trust services ───────────────────────────────────────────────
export const TRUST_PROVIDER_RECOGNITION_STATUSES = [
    'recognised',
    'not_recognised',
    'pending',
    'withdrawn',
    'unknown',
];
/**
 * The credential lifecycle.
 *
 * Hardcoded, and item 1 of the four universals at the top of this file. A
 * credential is issued, is active while it may be relied on, may be suspended
 * and restored, may be revoked irreversibly, and expires when its validity
 * period ends. Renewal is not a state — it produces a **successor credential**,
 * which is why `renewed_by_credential_id` is a pointer and not a status.
 */
export const TRUST_CREDENTIAL_STATUSES = [
    'issued',
    'active',
    'suspended',
    'revoked',
    'expired',
];
/**
 * The transitions the platform permits, as an explicit map.
 *
 * Written as data rather than as a chain of `if`s so the rule is readable, is
 * testable in one assertion, and cannot drift between the service that enforces
 * it and the interface that renders the available actions from it.
 *
 * `revoked` and `expired` are terminal. Revocation is irreversible by design —
 * a revoked credential that could be restored would make every relying party's
 * cached revocation check wrong. An expiry entered in error is corrected by
 * recording a renewal, which is a new credential with its own evidence, not by
 * quietly reopening the old one.
 */
export const TRUST_CREDENTIAL_TRANSITIONS = {
    issued: ['active', 'revoked', 'expired'],
    active: ['suspended', 'revoked', 'expired'],
    suspended: ['active', 'revoked', 'expired'],
    revoked: [],
    expired: [],
};
export const TRUST_EVENT_KINDS = [
    /** A credential changed status. Carries `to_status`. */
    'credential_lifecycle',
    /** A signature, seal, timestamp or data message was checked. */
    'verification',
];
export const TRUST_VERIFICATION_RESULTS = ['valid', 'invalid', 'indeterminate'];
/**
 * What was checked, when a `verification` event records a check.
 *
 * Open-ended in the same way `TrustServiceType` is: which of these a regime
 * recognises is declared, and a regime that recognises none of them simply
 * carries no service types. The list is the set of artefacts a verification can
 * be *about*, which is a technical taxonomy, not a legal one.
 */
export const TRUST_VERIFICATION_SUBJECTS = [
    'signature',
    'seal',
    'timestamp',
    'data_message',
    'certificate_path',
];
// ── Electronic submissions ───────────────────────────────────────
export const ELECTRONIC_SUBMISSION_STATUSES = [
    'draft',
    'submitted',
    'acknowledged',
    'rejected',
    'withdrawn',
];
// ── Electronic agreements ────────────────────────────────────────
export const AGREEMENT_PARTY_ROLES = ['issuer', 'counterparty', 'witness'];
export const AGREEMENT_SYNC_RESULTS = ['pending', 'succeeded', 'failed'];
export const AGREEMENT_STATUSES = [
    'draft',
    'signed',
    'active',
    'terminated',
    'superseded',
];
// ── Collections ──────────────────────────────────────────────────
export const IDENTITY_PROFILE_COLLECTION = 'grc_identity_profiles';
export const IDENTITY_ATTRIBUTE_COLLECTION = 'grc_identity_attributes';
export const IDENTITY_EVENT_COLLECTION = 'grc_identity_events';
export const TRUST_PROVIDER_COLLECTION = 'grc_trust_service_providers';
export const TRUST_CREDENTIAL_COLLECTION = 'grc_trust_credentials';
export const TRUST_EVENT_COLLECTION = 'grc_trust_events';
export const ELECTRONIC_SUBMISSION_COLLECTION = 'grc_electronic_submissions';
export const ELECTRONIC_AGREEMENT_COLLECTION = 'grc_electronic_agreements';
// ── Errors ───────────────────────────────────────────────────────
/**
 * Every error this module can produce, one code per distinct condition.
 *
 * 62.4.0's gate E12.5 found five conditions sharing one code, and then a second
 * pass found two codes each thrown at two different HTTP statuses — which leaves
 * a consumer unable to tell "your request was wrong" from "this tenant's
 * installed content is broken". The first is fixed by changing a request; the
 * second cannot be retried at all. The split below is written that way from the
 * start, and a guard test asserts every code is thrown somewhere in the module.
 */
export const TRUST_EVIDENCE_ERROR_CODES = {
    /** No installed framework declares a trust regime. */
    NO_REGIME_INSTALLED: 'no_trust_regime_installed',
    REGIME_NOT_FOUND: 'trust_regime_not_found',
    /** An installed regime's own content is malformed — a `409`, repair the pack. */
    REGIME_INVALID: 'trust_regime_invalid',
    /** The caller named a code the regime does not declare — a `400`. */
    SERVICE_TYPE_NOT_DECLARED: 'trust_service_type_not_declared',
    ASSURANCE_LEVEL_NOT_DECLARED: 'trust_assurance_level_not_declared',
    VERIFICATION_METHOD_NOT_DECLARED: 'trust_verification_method_not_declared',
    ATTRIBUTE_TYPE_NOT_DECLARED: 'identity_attribute_type_not_declared',
    AGREEMENT_TYPE_NOT_DECLARED: 'electronic_agreement_type_not_declared',
    /** The regime declares the method is not accepted for this attribute type. */
    METHOD_NOT_PERMITTED_FOR_ATTRIBUTE: 'verification_method_not_permitted_for_attribute',
    IDENTITY_PROFILE_NOT_FOUND: 'identity_profile_not_found',
    IDENTITY_ATTRIBUTE_NOT_FOUND: 'identity_attribute_not_found',
    IDENTITY_EVENT_NOT_FOUND: 'identity_event_not_found',
    /** An `attribute_verification` event with no attribute, or the reverse. */
    EVENT_KIND_MISMATCH: 'identity_event_kind_mismatch',
    /**
     * `impersonation_suspected: true` with no note, or a note with
     * `impersonation_suspected: null`. One code for both, because both are the
     * same mistake: the note and the assessment disagree.
     */
    IMPERSONATION_NOTE_MISMATCH: 'identity_event_impersonation_note_mismatch',
    /**
     * The holder is the profile itself, is not a person or legal entity, is on a
     * natural-person profile, or was given without a role (or a role without it).
     */
    HOLDER_INVALID: 'identity_holder_invalid',
    PROVIDER_NOT_FOUND: 'trust_provider_not_found',
    CREDENTIAL_NOT_FOUND: 'trust_credential_not_found',
    /**
     * The credential resolved, and has no recorded lifecycle to export — a `409`.
     *
     * Gate E12.5, F5. The export refusal used to throw `CREDENTIAL_NOT_FOUND` at
     * 409 for a credential it had **just successfully read**, which is the one
     * thing that code must never mean. A consumer branching on it would conclude
     * the id was wrong and retry with a different one forever; the actual repair
     * is to record an event.
     *
     * Exactly the defect 62.4.0's own error block was written against — "a code
     * that means five things is a distinction no consumer can make" — reproduced
     * in a module that copied the rule and then broke it four codes later.
     */
    CREDENTIAL_HAS_NO_LIFECYCLE: 'trust_credential_has_no_lifecycle',
    CREDENTIAL_EXISTS: 'trust_credential_exists',
    /** The transition is not in `TRUST_CREDENTIAL_TRANSITIONS` — a `409`. */
    TRANSITION_NOT_PERMITTED: 'trust_credential_transition_not_permitted',
    /** `suspended` and `revoked` require a reason. */
    TRANSITION_REASON_REQUIRED: 'trust_credential_transition_reason_required',
    /** A renewal named a predecessor that already has a successor. */
    CREDENTIAL_ALREADY_RENEWED: 'trust_credential_already_renewed',
    /**
     * The regime requires a recognised provider for this service type and the
     * provider's recorded recognition status is not `recognised`.
     */
    PROVIDER_NOT_RECOGNISED: 'trust_provider_not_recognised',
    SUBMISSION_NOT_FOUND: 'electronic_submission_not_found',
    /** The framework's own declaration refuses this channel. See `FilingChannelResolution`. */
    CHANNEL_NOT_ALLOWED: 'electronic_submission_channel_not_allowed',
    SUBMISSION_NOT_DRAFT: 'electronic_submission_not_draft',
    SUBMISSION_NOT_SUBMITTED: 'electronic_submission_not_submitted',
    RESPONSIBLE_USER_REQUIRED: 'electronic_submission_responsible_user_required',
    INVALID_PAYLOAD_HASH: 'electronic_submission_invalid_payload_hash',
    AGREEMENT_NOT_FOUND: 'electronic_agreement_not_found',
    AGREEMENT_REFERENCE_EXISTS: 'electronic_agreement_reference_exists',
    /**
     * The agreement named is a superseded version, so nothing may be written to
     * it — a `409`, and the remedy is to act on the version in force.
     *
     * Gate E12.5 pass 2. `updateAgreement` never inspected `existing.status`, so
     * signature evidence could be attached to a version that had been replaced
     * while the version actually in force still showed its unsigned warning. Its
     * own code rather than `AGREEMENT_NOT_FOUND`: the record is there, and a
     * client told "not found" would go looking for an installation problem.
     */
    AGREEMENT_SUPERSEDED: 'electronic_agreement_superseded',
    /**
     * A caller asked for a status only the platform may reach — a `400`.
     *
     * Gate E12.5 pass 3. `superseded` is in `AGREEMENT_STATUSES` and status is an
     * in-place field, so `PATCH {"status":"superseded"}` wrote it onto the version
     * *in force*, with no successor and nothing pointing at it — and the
     * superseded guard then refused every subsequent write, including the only
     * corrections available. A one-way door reachable in one request.
     *
     * `superseded` is a state the system **reaches** by minting a successor, never
     * one a caller chooses.
     */
    AGREEMENT_STATUS_NOT_SETTABLE: 'electronic_agreement_status_not_settable',
    /**
     * A `signature_event_ids` entry that resolves to nothing, belongs to another
     * organisation, is not a `verification`, or contradicts the agreement it is
     * being attached to — a `400`, and the remedy is always to send a different
     * id. `details.reason` distinguishes them for a reader.
     */
    SIGNATURE_EVENT_INVALID: 'electronic_agreement_signature_event_invalid',
    /** A party naming zero references, or more than one. */
    PARTY_REFERENCE_INVALID: 'electronic_agreement_party_reference_invalid',
    AGREEMENT_EXCEPTION_NOT_FOUND: 'electronic_agreement_exception_not_found',
    AGREEMENT_EXCEPTION_RESOLVED: 'electronic_agreement_exception_already_resolved',
    /** The regime's agreement type declares no sync obligation. */
    SYNC_NOT_REQUIRED: 'electronic_agreement_sync_not_required',
    /** A referenced canonical record resolves to nothing in this organisation. */
    LINK_NOT_FOUND: 'trust_evidence_link_not_found',
    OWNER_NOT_FOUND: 'trust_evidence_owner_not_found',
    FRAMEWORK_NOT_INSTALLED: 'trust_evidence_framework_not_installed',
    INVALID_DATE: 'trust_evidence_invalid_date',
    /** `valid_until` before `valid_from`, or an amendment before its submission. */
    DATE_RANGE_INVALID: 'trust_evidence_date_range_invalid',
};
//# sourceMappingURL=trust-evidence.js.map